import {
  GroupByMode,
  ID,
  newTaskSuffix,
  overdueSectionID,
} from '@griply/types';
import {
  getNewTaskPrefill,
  getTaskIdFromTreeId,
  getUpcomingSectionDate,
} from '@griply/utils';
import React from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  TaskListItemTypes,
} from 'shared/components/ui/virtualized-tree-list';

import {
  upcomingMonthPrefix,
  upcomingRestOfMonthPrefix,
  upcomingYearPrefix,
} from '../../hooks/use-upcoming-deadline-tree';
import { TreeGoalRow } from '../tree-goal-row';
import { TreeTaskCard } from '../tree-task-card';
import { UpcomingDeadlineHeader } from '../upcoming-deadline-header';
import { useUpcomingDeadlineRenderData } from './upcoming-deadline-render-context';

const sectionPrefixes = {
  restOfMonth: upcomingRestOfMonthPrefix,
  month: upcomingMonthPrefix,
  year: upcomingYearPrefix,
};

const isDaySectionId = (sectionId: ID): boolean =>
  sectionId !== overdueSectionID &&
  !sectionId.startsWith(upcomingRestOfMonthPrefix) &&
  !sectionId.startsWith(upcomingMonthPrefix) &&
  !sectionId.startsWith(upcomingYearPrefix);

export const UpcomingDeadlineRenderItem: React.FC<RenderItemProps> = ({
  item,
  itemInstance,
  tree,
  isMultiSelectModifierPressed,
}) => {
  const {
    tasksMap,
    tasks,
    overdueTasks,
    externalEvents,
    today,
    denseFormat,
    displayDue,
    timeFormat,
    showCompleted,
    goalRenderData,
  } = useUpcomingDeadlineRenderData();

  const type = item.type;

  if (type === TaskListItemTypes.GoalDeadline) {
    return goalRenderData ? (
      <TreeGoalRow sourceId={item.sourceId} data={goalRenderData} />
    ) : null;
  }

  if (type === TaskListItemTypes.Task) {
    const parentElement = itemInstance.getParent();
    const parentId = parentElement?.getItemData().sourceId;

    const sectionDate = parentId
      ? getUpcomingSectionDate(parentId, sectionPrefixes)
      : undefined;
    const isDay = parentId ? isDaySectionId(parentId) : false;

    const effectiveDisplayDue =
      parentId === overdueSectionID || !isDay ? displayDue : [];

    const taskId = getTaskIdFromTreeId(item.sourceId);
    const task = tasksMap[taskId];
    if (!task) {
      return null;
    }

    return (
      <TreeTaskCard
        task={task}
        tree={tree}
        item={itemInstance}
        tasks={tasks}
        isMultiSelectModifierPressed={isMultiSelectModifierPressed}
        completionDate={isDay ? sectionDate : undefined}
        displayDate={isDay ? sectionDate : undefined}
        displayDue={effectiveDisplayDue}
        includeCompletedOccurrence={isDay && showCompleted}
      />
    );
  }

  if (type === TaskListItemTypes.NewTask) {
    const groupSectionId = item.sourceId.replace(newTaskSuffix, '');
    const prefill = getNewTaskPrefill({
      groupByMode: GroupByMode.UpcomingDeadline,
      groupSectionId,
      groupSetting: {},
      today,
    });
    return (
      <NewTaskRow
        date={prefill.startDate}
        dateAnchored={prefill.dateAnchored}
        startTime={null}
        duration={null}
      />
    );
  }

  if (type === TaskListItemTypes.Date) {
    return (
      <UpcomingDeadlineHeader
        denseFormat={denseFormat}
        item={itemInstance}
        overdueTasks={overdueTasks}
        externalEvents={externalEvents}
        timeFormat={timeFormat}
      />
    );
  }

  return null;
};
